import React, { useContext, useEffect, useState } from 'react';
import { createSearchParams, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
	Wrapper,
	ContentWrapper,
	PageLead,
	Toast,
	ErrorCrossIcon,
	withLocalization,
	ContentLoader,
} from '@adac-mp/components';
import {
	checkPayOneStatus,
	completeOrder,
	Variants,
	getVignettesOrderCompletedCollection,
	IOfferContext,
	ITypeVignettesOrderCompletedFields,
	IVignetteContext,
	OfferContext,
	onOrderCompleted,
	VignetteContext,
	getLabelValue,
	OrderService,
	getErrorTextFromResponse,
	getUrlBasePath,
} from '@adac-mp/shared';
import Lottie from 'lottie-react';
import { gatherProductTrackingData } from '@adac-mp/components/src/utils/tracking/tracking';
import carAnimation from '../assets/lottie/adac-car-loading.json';
import camperAnimation from '../assets/lottie/adac-camper-loading.json';

const Processing = () => {
	const navigate = useNavigate();
	const { lang, labels, updateOrderInfo } = useContext<IOfferContext>(OfferContext);
	const { contentfulLabels, updateContentfulLabels } = useContext<IVignetteContext>(VignetteContext);
	const [apiError, setApiError] = useState<string | undefined>(undefined);
	const [variant, setVariant] = useState<Variants | null>(null);
	const { type } = useParams();

	const { error, data, loading } = useQuery(getVignettesOrderCompletedCollection, {
		variables: { lang: lang || 'de' },
	});

	const getVariant = () => {
		if (type) {
			if (lang) return type as Variants;
			return type.split('&')[0] as Variants;
		}
		return null;
	};
	useEffect(() => {
		if (!error) {
			updateContentfulLabels(data?.vignettesOrderCompletedCollection.items[0]);
		}
	}, [data]);

	const currentLabels = contentfulLabels as ITypeVignettesOrderCompletedFields;

	useEffect(() => {
		setVariant(getVariant());
	}, []);

	const processPaymentUpdate = (orderHash: string, offerHash: string, customerHash: string, token: string) => {
		OrderService.amendOrderPayment({ orderHash, offerHash, customerHash }, token).then(
			({ code, orderInfo, errors }) => {
				if (code === 200 && orderInfo) {
					sessionStorage.setItem('orderInfo', JSON.stringify(orderInfo));
					const params = createSearchParams();
					params.set('type', 'updatepayment');
					navigate(getUrlBasePath('/order-confirmation', params));
				} else {
					const errorText = getErrorTextFromResponse(errors, getLabelValue('mp.generalError', labels));
					setApiError(errorText);
				}
			},
		);
	};

	const processOrder = (
		offerHash: string | null,
		cartHash: string | null,
		token: string,
		customerHash: string,
		currentVariant: Variants,
	) => {
		checkPayOneStatus(offerHash, cartHash, token, 3000)
			.then((res) => {
				let orderDetails = {};
				const order = sessionStorage.getItem('orderDetail');
				if (order) {
					orderDetails = JSON.parse(order);
				}

				if (res && customerHash) {
					completeOrder({
						lang,
						offerHash: res.offerHash,
						additionalData: orderDetails,
						variant: currentVariant,
						errorLabel: getLabelValue('mp.generalError', labels),
						updateOrderInfo,
						onOrderCompleted: onOrderCompleted(navigate),
						gatherProductTrackingData,
						setApiError,
					});
				}
			})
			.catch(() => {
				const params = createSearchParams();
				params.set('type', 'payment');
				navigate(getUrlBasePath('/error', params), { replace: true });
			});
	};

	useEffect(() => {
		const offerHash = sessionStorage.getItem('offerHash');
		const cartHash = sessionStorage.getItem('cartHash');
		const customerHash = sessionStorage.getItem('customerHash');
		const orderHash = sessionStorage.getItem('orderHash');
		if (variant) {
			if (variant === 'updatepayment' && offerHash && customerHash && orderHash) {
				const token = sessionStorage.getItem('token') ?? '';
				processPaymentUpdate(orderHash, offerHash, customerHash, token);
			} else if ((cartHash || offerHash) && customerHash) {
				const token = sessionStorage.getItem('cartToken') || sessionStorage.getItem('customerToken') || '';
				processOrder(offerHash, cartHash, token, customerHash, variant);
			} else {
				window.location.href = 'https://www.adac.de/fahrzeugwelt/maut-vignette/mautbox';
			}
		}
	}, [variant]);

	return (
		<>
			{loading ? (
				<ContentLoader isLoading hasPortal={false} />
			) : (
				<>
					{apiError && (
						<Toast
							content={apiError}
							icon={<ErrorCrossIcon />}
							onToastFinished={() => setApiError(undefined)}
						/>
					)}
					<Wrapper className="bg-white min-h-[calc(100vh_-_56px)] sm:min-h-[calc(100vh_-_98px)]">
						<ContentWrapper>
							<PageLead
								icon={<Lottie animationData={type === 'dkv' ? camperAnimation : carAnimation} />}
								headline={currentLabels?.processingHeadline || 'Wir beeilen uns.'}
								description={
									currentLabels?.processingText ||
									'Ihre Transaktion wird gerade abgewickelt. Bitte verlassen Sie solange diese Seite nicht.'
								}
							/>
						</ContentWrapper>
					</Wrapper>
				</>
			)}
		</>
	);
};

export const ProcessingPage = withLocalization(Processing);
